// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useState } from 'react'

import type { KnowledgeBaseItem } from '../types'

export interface ItemDraftState {
  content: string
  score: string
  sectionType: string
  sectionLabel: string
  fields: Record<string, string>
}

const DEFAULT_DRAFT: ItemDraftState = {
  content: '',
  score: '1',
  sectionType: '',
  sectionLabel: '',
  fields: {},
}

export interface UseItemDraftResult extends ItemDraftState {
  update: (updates: Partial<ItemDraftState>) => void
  updateField: (key: string, value: string) => void
  reset: (state?: Partial<ItemDraftState>) => void
  loadFromItem: (item: KnowledgeBaseItem) => void
}

export function useItemDraft(initial?: Partial<ItemDraftState>): UseItemDraftResult {
  const [draft, setDraft] = useState<ItemDraftState>({ ...DEFAULT_DRAFT, ...initial })

  const update = useCallback((updates: Partial<ItemDraftState>) => {
    setDraft(prev => ({ ...prev, ...updates }))
  }, [])

  const updateField = useCallback((key: string, value: string) => {
    setDraft(prev => ({ ...prev, fields: { ...prev.fields, [key]: value } }))
  }, [])

  const reset = useCallback((state?: Partial<ItemDraftState>) => {
    setDraft({ ...DEFAULT_DRAFT, ...state })
  }, [])

  const loadFromItem = useCallback((item: KnowledgeBaseItem) => {
    const metadata = item.metadata ?? {}
    setDraft({
      content: item.content,
      score: item.score ? String(item.score) : '1',
      sectionType: typeof metadata.sectionType === 'string' ? metadata.sectionType : '',
      sectionLabel: typeof metadata.sectionLabel === 'string' ? metadata.sectionLabel : '',
      fields: item.fields ?? {},
    })
  }, [])

  return {
    ...draft,
    update,
    updateField,
    reset,
    loadFromItem,
  }
}
